<script setup>
import { ref, onMounted, computed, nextTick, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { useUsuarioStore } from '@/store/usuarioStore';
import { useListadosEntidadesStore } from '@/store/listadosEntidadesStore';
import usuarioModel from '@/models/usuarioModel';
import { showAlert } from '@/utils/alerts';
import * as bootstrap from 'bootstrap';
import FileInput from '@/components/Inputs/FileInput.vue';
import { attachPlacesAutocomplete, matchProvinciaIdByName, parsePlaceAddress } from '@/utils/googlePlaces';

const usuarioStore = useUsuarioStore();
const listadosEntidadesStore = useListadosEntidadesStore();
const router = useRouter();

const makeInitials = (primary, secondary) => {
  const first = (primary ?? '').trim();
  const second = (secondary ?? '').trim();

  if (first && second) return `${first[0]}${second[0]}`.toUpperCase();

  const single = (first || second).replaceAll(/\s+/g, '');
  return (single.slice(0, 2) || 'NU').toUpperCase();
};

const deepClone = (value) => structuredClone(value);

const displayName = computed(() => {
  const alias = (perfil.value?.alias ?? usuarioStore.user?.alias ?? '').trim();
  if (alias) return alias;

  const name = (perfil.value?.name ?? usuarioStore.user?.name ?? '').trim();
  const lastname = (perfil.value?.lastname ?? usuarioStore.user?.lastname ?? '').trim();
  const full = [name, lastname].filter(Boolean).join(' ').trim();

  return full || 'Nuevo Usuario';
});

const initialsText = computed(() => {
  const alias = (perfil.value?.alias ?? usuarioStore.user?.alias ?? '').trim();
  if (alias) {
    const parts = alias.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return makeInitials(parts[0], parts[1]);
    return makeInitials(alias, '');
  }

  const name = (perfil.value?.name ?? usuarioStore.user?.name ?? '').trim();
  const lastname = (perfil.value?.lastname ?? usuarioStore.user?.lastname ?? '').trim();
  if (name || lastname) return makeInitials(name, lastname);

  return 'NU';
});

const normalizeComoNosConocieronId = (value) => (value == null || value === '' ? '' : String(value));

const perfil = ref({
  name: '',
  lastname: '',
  alias: '',
  razon_social: '',
  id: '',
  email: '',
  cuit: '',
  celular: '',
  direccion: '',
  ciudad: '',
  codigo_postal: '',
  provincia_id: '',
  telefono_fijo: '',
  como_nos_conocieron_id: '',
  dni: '',
  foto_perfil: ''
});

const perfilOriginal = ref(null);

const provincias = computed(() => listadosEntidadesStore?.provincias ?? []);

const direccionInputEl = ref(null);
const cleanupPlaces = ref(() => {});

const showTopAlert = ref(true);

// Modal Foto de perfil
const photoModalRef = ref(null);
const photoModalInstance = ref(null);
const photoUploading = ref(false);
const selectedPhotoFile = ref(null);
const photoPreview = ref('');
const photoObjectUrl = ref('');

// Editor de encuadre (drag + zoom)
const editorFrameSize = 220;
const photoScale = ref(1);
const photoOffsetX = ref(0);
const photoOffsetY = ref(0);
const photoNaturalW = ref(0);
const photoNaturalH = ref(0);
const dragging = ref(false);
const dragStart = ref({ x: 0, y: 0, ox: 0, oy: 0 });

const revokeObjectUrl = () => {
  if (photoObjectUrl.value) {
    URL.revokeObjectURL(photoObjectUrl.value);
    photoObjectUrl.value = '';
  }
};

const clamp = (val, min, max) => Math.min(max, Math.max(min, val));

const getBaseCoverSize = () => {
  const w = photoNaturalW.value || 1;
  const h = photoNaturalH.value || 1;
  const aspect = w / h;

  // Cover en un frame cuadrado
  if (aspect >= 1) {
    return { baseW: editorFrameSize * aspect, baseH: editorFrameSize };
  }
  return { baseW: editorFrameSize, baseH: editorFrameSize / aspect };
};

const clampOffsets = () => {
  const { baseW, baseH } = getBaseCoverSize();
  const drawW = baseW * photoScale.value;
  const drawH = baseH * photoScale.value;
  const maxX = Math.max(0, (drawW - editorFrameSize) / 2);
  const maxY = Math.max(0, (drawH - editorFrameSize) / 2);
  photoOffsetX.value = clamp(photoOffsetX.value, -maxX, maxX);
  photoOffsetY.value = clamp(photoOffsetY.value, -maxY, maxY);
};

const resetPhotoEditor = () => {
  photoScale.value = 1;
  photoOffsetX.value = 0;
  photoOffsetY.value = 0;
};

const resetPhotoModalState = () => {
  selectedPhotoFile.value = null;
  photoPreview.value = '';
  revokeObjectUrl();
  photoNaturalW.value = 0;
  photoNaturalH.value = 0;
  resetPhotoEditor();
  photoUploading.value = false;
};

const openPhotoModal = async (event) => {
  if (event?.preventDefault) event.preventDefault();

  await nextTick();
  if (!photoModalInstance.value && photoModalRef.value) {
    photoModalInstance.value = new bootstrap.Modal(photoModalRef.value, {
      backdrop: 'static',
      keyboard: false,
    });
  }

  // Preview inicial: foto actual si existe
  photoPreview.value = perfil.value.foto_perfil || '';
  revokeObjectUrl();
  photoNaturalW.value = 0;
  photoNaturalH.value = 0;
  resetPhotoEditor();
  selectedPhotoFile.value = null;
  photoModalInstance.value?.show();
};

const closePhotoModal = () => {
  photoModalInstance.value?.hide();
};

const loadImageMeta = (src) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = reject;
    img.src = src;
  });
};

const onPhotoFileSelected = async (file) => {
  if (!file) return;

  if (!file.type?.startsWith('image/')) {
    showAlert('danger', 'El archivo debe ser una imagen.');
    return;
  }

  // Límite blando de archivo: 10MB (luego la comprimimos para cumplir el límite del endpoint ~5MB)
  const MAX_BYTES = 10 * 1024 * 1024;
  if (file.size > MAX_BYTES) {
    showAlert('danger', 'La imagen es demasiado grande (máx. 10MB).');
    return;
  }

  try {
    photoUploading.value = true;
    selectedPhotoFile.value = file;

    // Usamos objectURL para preview fluida y para obtener dimensiones
    revokeObjectUrl();
    const url = URL.createObjectURL(file);
    photoObjectUrl.value = url;
    photoPreview.value = url;

    const meta = await loadImageMeta(url);
    photoNaturalW.value = meta.width;
    photoNaturalH.value = meta.height;
    resetPhotoEditor();
    clampOffsets();
  } catch (error) {
    console.error('Error al procesar imagen', error);
    showAlert('danger', 'No se pudo procesar la imagen.');
  } finally {
    photoUploading.value = false;
  }
};

const onEditorPointerDown = (e) => {
  if (!photoPreview.value) return;
  dragging.value = true;
  dragStart.value = {
    x: e.clientX,
    y: e.clientY,
    ox: photoOffsetX.value,
    oy: photoOffsetY.value,
  };
  e.currentTarget?.setPointerCapture?.(e.pointerId);
};

const onEditorPointerMove = (e) => {
  if (!dragging.value) return;
  const dx = e.clientX - dragStart.value.x;
  const dy = e.clientY - dragStart.value.y;
  photoOffsetX.value = dragStart.value.ox + dx;
  photoOffsetY.value = dragStart.value.oy + dy;
  clampOffsets();
};

const onEditorPointerUp = (e) => {
  if (!dragging.value) return;
  dragging.value = false;
  e.currentTarget?.releasePointerCapture?.(e.pointerId);
};

const onZoomInput = () => {
  clampOffsets();
};

const estimateBase64Bytes = (base64) => {
  if (!base64) return 0;
  const len = base64.length;
  let padding = 0;
  if (base64.endsWith('==')) padding = 2;
  else if (base64.endsWith('=')) padding = 1;
  return Math.floor((len * 3) / 4) - padding;
};

const extractBase64FromDataUrl = (dataUrl) => {
  if (!dataUrl || typeof dataUrl !== 'string') return '';
  const comma = dataUrl.indexOf(',');
  if (comma === -1) return '';
  return dataUrl.slice(comma + 1);
};

const renderEditedImageToDataUrl = async ({ outputSize = 512, quality = 0.9 } = {}) => {
  if (!selectedPhotoFile.value || !photoPreview.value) return null;

  const canvas = document.createElement('canvas');
  canvas.width = outputSize;
  canvas.height = outputSize;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  const img = new Image();
  img.src = photoPreview.value;
  await new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = reject;
  });

  // Base cover en unidades del editor, luego escalamos a output
  const { baseW, baseH } = getBaseCoverSize();
  const ratio = outputSize / editorFrameSize;
  const drawW = baseW * photoScale.value * ratio;
  const drawH = baseH * photoScale.value * ratio;
  const ox = photoOffsetX.value * ratio;
  const oy = photoOffsetY.value * ratio;

  const x = (outputSize - drawW) / 2 + ox;
  const y = (outputSize - drawH) / 2 + oy;

  ctx.clearRect(0, 0, outputSize, outputSize);
  ctx.drawImage(img, x, y, drawW, drawH);

  // JPEG para tamaño menor (si necesitas PNG transparente, lo cambiamos)
  return canvas.toDataURL('image/jpeg', quality);
};

const saveProfilePhoto = async () => {
  try {
    if (!photoPreview.value) {
      showAlert('danger', 'Seleccioná una imagen para continuar.');
      return;
    }

    // Si no seleccionó un archivo nuevo, no hay nada para subir.
    if (!selectedPhotoFile.value) {
      showAlert('info', 'No seleccionaste una nueva imagen.');
      return;
    }

    photoUploading.value = true;

    // Endpoint: POST /website/usuarios/perfil/foto
    // Enviamos base64 + extensión, cuidando el límite aprox de 5MB.
    const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;
    let outputSize = 512;
    let quality = 0.9;
    let dataUrl = await renderEditedImageToDataUrl({ outputSize, quality });
    if (!dataUrl) {
      showAlert('danger', 'No se pudo procesar la imagen.');
      return;
    }

    let base64 = extractBase64FromDataUrl(dataUrl);
    let bytes = estimateBase64Bytes(base64);

    // Reducimos calidad/tamaño si el base64 excede el límite.
    while (bytes > MAX_UPLOAD_BYTES && quality > 0.6) {
      quality = Math.max(0.6, Number((quality - 0.1).toFixed(2)));
      dataUrl = await renderEditedImageToDataUrl({ outputSize, quality });
      base64 = extractBase64FromDataUrl(dataUrl);
      bytes = estimateBase64Bytes(base64);
    }
    if (bytes > MAX_UPLOAD_BYTES && outputSize > 384) {
      outputSize = 384;
      quality = 0.8;
      dataUrl = await renderEditedImageToDataUrl({ outputSize, quality });
      base64 = extractBase64FromDataUrl(dataUrl);
      bytes = estimateBase64Bytes(base64);
    }

    if (bytes > MAX_UPLOAD_BYTES) {
      showAlert('danger', 'La imagen sigue siendo demasiado pesada para subirla (límite aprox. 5MB). Probá con una foto más liviana.');
      return;
    }

    await usuarioModel.uploadProfilePhoto({ archivo: base64, extension: 'jpg' });

    // Refrescar session y actualizar UI
    await usuarioStore.getSession();
    const user = usuarioStore.user;

    // Fallback: si session no trae foto_perfil, la aplicamos localmente para que Navbar/Perfil reflejen el cambio.
    const sessionPhoto = user?.foto_perfil ?? user?.perfil?.foto_perfil;
    if (!sessionPhoto) {
      usuarioStore.user = {
        ...(user ?? undefined),
        foto_perfil: dataUrl,
        perfil: {
          ...(user?.perfil ?? undefined),
          foto_perfil: dataUrl,
        },
      };
      try {
        localStorage.setItem('user', JSON.stringify(usuarioStore.user));
      } catch (e) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('[user] No se pudo persistir en localStorage', e);
        }
      }
    }

    const refreshedUser = usuarioStore.user;
    perfil.value = {
      ...perfil.value,
      ...(refreshedUser ?? undefined),
      ...(refreshedUser?.perfil ?? undefined),
    };

    showAlert('success', 'Foto de perfil actualizada.');

    // Mantener snapshot de edición
    perfilOriginal.value = deepClone(perfil.value);

    closePhotoModal();
    resetPhotoModalState();
  } catch (error) {
    const message = error?.response?.data?.message || error?.response?.data?.error || 'No se pudo actualizar la foto.';
    showAlert('danger', message);
  } finally {
    photoUploading.value = false;
  }
};

onMounted(() => {
  // Limpieza al cerrar modal (si el usuario lo cierra desde UI)
  if (photoModalRef.value) {
    photoModalRef.value.addEventListener('hidden.bs.modal', () => {
      resetPhotoModalState();
    });
  }
  if (passwordModalRef.value) {
    passwordModalRef.value.addEventListener('hidden.bs.modal', () => {
      resetPasswordForm();
    });
  }
});

onBeforeUnmount(() => {
  try {
    photoModalInstance.value?.dispose?.();
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') {
      console.debug('[photoModal] dispose falló', e);
    }
  }
  try {
    passwordModalInstance.value?.dispose?.();
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') {
      console.debug('[passwordModal] dispose falló', e);
    }
  }
  try {
    confirmPasswordModalInstance.value?.dispose?.();
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') {
      console.debug('[confirmPasswordModal] dispose falló', e);
    }
  }
  try {
    if (typeof cleanupPlaces.value === 'function') cleanupPlaces.value();
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') {
      console.debug('[places] cleanup falló', e);
    }
  }
  revokeObjectUrl();
});

// Modal Cambiar contraseña
const passwordModalRef = ref(null);
const passwordModalInstance = ref(null);
const passwordForm = ref({
  password: '',
  new_password: '',
  new_password_confirmation: '',
});
const passwordSaving = ref(false);
const showCurrentPassword = ref(false);
const showNewPassword = ref(false);
const showConfirmPassword = ref(false);

// Modal Confirmación de cambio de contraseña
const confirmPasswordModalRef = ref(null);
const confirmPasswordModalInstance = ref(null);

const resetPasswordForm = () => {
  passwordForm.value = { password: '', new_password: '', new_password_confirmation: '' };
  passwordSaving.value = false;
  showCurrentPassword.value = false;
  showNewPassword.value = false;
  showConfirmPassword.value = false;
};

const openPasswordModal = async (event) => {
  if (event?.preventDefault) event.preventDefault();
  await nextTick();
  if (!passwordModalInstance.value && passwordModalRef.value) {
    passwordModalInstance.value = new bootstrap.Modal(passwordModalRef.value, {
      backdrop: 'static',
      keyboard: false,
    });
  }
  resetPasswordForm();
  passwordModalInstance.value?.show();
};

const closePasswordModal = () => {
  passwordModalInstance.value?.hide();
};

const openConfirmPasswordModal = () => {
  // Validaciones locales antes de pedir confirmación
  const { password, new_password, new_password_confirmation } = passwordForm.value;
  if (!password || !new_password || !new_password_confirmation) {
    showAlert('danger', 'Completá todos los campos.');
    return;
  }
  if (new_password.length < 6) {
    showAlert('danger', 'La nueva contraseña debe tener al menos 6 caracteres.');
    return;
  }
  if (new_password !== new_password_confirmation) {
    showAlert('danger', 'La nueva contraseña y su confirmación no coinciden.');
    return;
  }

  // Abrir modal de confirmación
  if (!confirmPasswordModalInstance.value && confirmPasswordModalRef.value) {
    confirmPasswordModalInstance.value = new bootstrap.Modal(confirmPasswordModalRef.value, {
      backdrop: 'static',
      keyboard: false,
    });
  }
  confirmPasswordModalInstance.value?.show();
};

const closeConfirmPasswordModal = () => {
  confirmPasswordModalInstance.value?.hide();
};

const submitChangePassword = async () => {
  try {
    passwordSaving.value = true;
    closeConfirmPasswordModal();

    await usuarioModel.changePassword({
      password: passwordForm.value.password,
      new_password: passwordForm.value.new_password,
      new_password_confirmation: passwordForm.value.new_password_confirmation,
    });

    showAlert('success', 'Contraseña actualizada con éxito.');
    closePasswordModal();
    resetPasswordForm();
  } catch (error) {
    const data = error?.response?.data;
    const message =
      data?.message ||
      data?.error ||
      (data?.errors ? Object.values(data.errors).flat().join(' ') : null) ||
      'No se pudo cambiar la contraseña.';
    showAlert('danger', message);
  } finally {
    passwordSaving.value = false;
  }
};

// Datos de facturación
const facturacionData = ref([]);
const MAX_FACTURACION_PERFILES = 5;

const facturacionLimitReached = computed(() => {
  const count = Array.isArray(facturacionData.value) ? facturacionData.value.length : 0;
  return count >= MAX_FACTURACION_PERFILES;
});

const provinciaNombreById = (id) => {
  const pid = Number(id);
  if (!Number.isFinite(pid)) return '';
  const list = listadosEntidadesStore?.provincias ?? [];
  const found = list.find((p) => Number(p.id) === pid);
  return found?.nombre ?? '';
};

const mapPerfilFacturacionToUI = (perfilFacturacion) => {
  const p = perfilFacturacion ?? {};
  const razonSocial = p.razon_social ?? '';

  const fileNameFromObj = (obj) => {
    if (!obj || typeof obj !== 'object') return '';
    return (
      obj.nombre ??
      obj.nombre_archivo ??
      obj.filename ??
      obj.original_name ??
      obj.originalName ??
      obj.name ??
      ''
    );
  };

  const fileDisplayFrom = ({ idField, objField }) => {
    const id = p?.[idField];
    if (id == null || id === '') return '';
    const fromObj = objField ? fileNameFromObj(p?.[objField]) : '';
    return (fromObj && String(fromObj).trim()) || 'Nombredelarchivo.jpg';
  };

  const inferTipo = () => {
    // Primero intentamos inferir por presencia de archivos (más confiable)
    const hasConsumidorFiles =
      p?.constrancia_cuil_archivo_id != null ||
      p?.constancia_cuil_archivo_id != null ||
      p?.dni_frente_archivo_id != null ||
      p?.dni_dorso_archivo_id != null;

    const hasResponsableFiles =
      p?.constancia_cuit_archivo_id != null ||
      p?.cm05_archivo_id != null ||
      p?.iibb_archivo_id != null;

    if (hasConsumidorFiles && !hasResponsableFiles) return 'consumidor';
    if (hasResponsableFiles && !hasConsumidorFiles) return 'responsable';

    const raw =
      p.tipo ??
      p.condicion_iva ??
      p.condicion_frente_iva ??
      p.condicion_iva_nombre ??
      p.iva;

    const text = (raw == null ? '' : String(raw)).toLowerCase();
    if (text.includes('responsable') || text.includes('inscrip')) return 'responsable';
    if (text.includes('consumidor')) return 'consumidor';

    // Heurística simple para aproximar el diseño cuando el backend no envía el tipo
    const name = String(razonSocial).toLowerCase();
    if (/(\bs\.a\b|\bs\.r\.l\b|\bs\.a\.s\b|\bsas\b|\bsrl\b|\bsa\b)/.test(name)) return 'responsable';
    return 'consumidor';
  };

  const hasConstanciaCuilId = p?.constancia_cuil_archivo_id !== null && p?.constancia_cuil_archivo_id !== undefined;
  const constanciaCuilIdField = hasConstanciaCuilId ? 'constancia_cuil_archivo_id' : 'constrancia_cuil_archivo_id';
  let constanciaCuilObjField = null;
  if (p?.constancia_cuil_archivo) constanciaCuilObjField = 'constancia_cuil_archivo';
  else if (p?.constrancia_cuil_archivo) constanciaCuilObjField = 'constrancia_cuil_archivo';

  const tipo = inferTipo();
  return {
    id: p.id,
    tipo,
    nombre_empresa: razonSocial,
    cuit: p.cuit_cuil ?? '',
    direccion: p.calle_numero ?? '',
    localidad: p.localidad ?? '',
    telefono_fijo: p.telefono_fijo ?? '',
    telefono_celular: p.telefono_celular ?? '',
    codigo_postal: p.codigo_postal ?? '',
    provincia_id: p.provincia_id ?? null,
    provincia: provinciaNombreById(p.provincia_id),
    isPredeterminada: Boolean(p.predeterminada),
    archivos: {
      // Consumidor final
      constancia_cuil: fileDisplayFrom({
        idField: constanciaCuilIdField,
        objField: constanciaCuilObjField,
      }),
      dni_frente: fileDisplayFrom({ idField: 'dni_frente_archivo_id', objField: 'dni_frente_archivo' }),
      dni_dorso: fileDisplayFrom({ idField: 'dni_dorso_archivo_id', objField: 'dni_dorso_archivo' }),

      // Responsable inscripto
      constancia_cuit: fileDisplayFrom({ idField: 'constancia_cuit_archivo_id', objField: 'constancia_cuit_archivo' }),
      cm05: fileDisplayFrom({ idField: 'cm05_archivo_id', objField: 'cm05_archivo' }),
      ingresos_brutos: fileDisplayFrom({ idField: 'iibb_archivo_id', objField: 'iibb_archivo' }),
    },
  };
};

const ivaBadge = (factura) => {
  const tipo = factura?.tipo === 'responsable' ? 'responsable' : 'consumidor';
  if (tipo === 'responsable') {
    return { text: 'RESPONSABLE INSCRIPTO', class: 'iva-badge--responsable' };
  }
  return { text: 'CONSUMIDOR FINAL', class: 'iva-badge--consumidor' };
};

const archivoLabelByTipo = (tipo) => {
  if (tipo === 'responsable') {
    return [
      { key: 'constancia_cuit', label: 'Constancia de Cuit' },
      { key: 'cm05', label: 'CM05' },
      { key: 'ingresos_brutos', label: 'Ingresos brutos' },
    ];
  }
  return [
    { key: 'constancia_cuil', label: 'Constancia de Cuil' },
    { key: 'dni_frente', label: '*Foto del frente de DNI' },
    { key: 'dni_dorso', label: '*Foto del dorso de DNI' },
  ];
};

const archivoStatus = (value) => {
  const file = value == null ? '' : String(value).trim();
  if (!file) return { ok: false, text: 'No se cargó ningún archivo' };
  return { ok: true, text: file };
};

const loadFacturacionData = async () => {
  try {
    const res = await usuarioModel.obtenerPerfilesFacturacionUsuario();
    const raw = res?.data ?? res;
    let list = [];
    if (Array.isArray(raw)) list = raw;
    else if (Array.isArray(raw?.data)) list = raw.data;
    facturacionData.value = list.map(mapPerfilFacturacionToUI);
  } catch (error) {
    console.error('[facturacion] No se pudieron cargar perfiles', error);
    facturacionData.value = [];
  }
};

onMounted(async () => {
  try {
    await usuarioStore.getSession();
    await listadosEntidadesStore.getProvincias();
    await loadFacturacionData();

    // Cargar datos del perfil (y mantener edición local independiente del refresh de sesión)
    const user = usuarioStore.user ?? {};
    const nestedPerfil = user.perfil ?? {};
    const comoNosConocieron = normalizeComoNosConocieronId(
      user.como_nos_conocieron_id ?? nestedPerfil.como_nos_conocieron_id ?? perfil.value.como_nos_conocieron_id
    );

    perfil.value = {
      ...perfil.value,
      ...user,
      ...nestedPerfil,
      como_nos_conocieron_id: comoNosConocieron,
    };

    // Snapshot para enviar solo cambios en el PUT /usuarios/perfil
    perfilOriginal.value = deepClone(perfil.value);

    await nextTick();
    const apiKey = process.env.VUE_APP_API_KEY_GMAPS || process.env.VUE_API_KEY_GMAPS;
    cleanupPlaces.value = await attachPlacesAutocomplete({
      inputEl: direccionInputEl.value,
      apiKey,
      country: 'ar',
      onSelect: (place) => {
        const parsed = parsePlaceAddress(place);
        const direccion = parsed?.direccion || place?.name || place?.formatted_address || '';
        if (direccion) perfil.value.direccion = direccion;
        if (parsed?.codigo_postal) perfil.value.codigo_postal = parsed.codigo_postal;
        if (parsed?.localidad) perfil.value.ciudad = parsed.localidad;

        const provinciaId = matchProvinciaIdByName(provincias.value, parsed?.provincia);
        if (provinciaId != null) perfil.value.provincia_id = String(provinciaId);
      },
    });
  } catch (error) {
    console.error("Error al cargar el usuario:", error);
  }
});

const opcionesComoNosConocieron = [
  { id: 1, nombre: "Publicidad en redes sociales" },
  { id: 2, nombre: "Recomendación de un amigo" },
  { id: 3, nombre: "Búsqueda en Google" },
  { id: 4, nombre: "Email o Newsletter" },
  { id: 5, nombre: "Otro" }
];

const closeAlert = () => {
  showTopAlert.value = false;
};

const toNullableNumber = (value) => {
  if (value == null || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const normalizeOptionalString = (value) => {
  if (value == null) return null;
  const text = String(value).trim();
  return text === '' ? null : text;
};

const buildUpdateProfilePayloadDiff = () => {
  const original = perfilOriginal.value || {};
  const current = perfil.value || {};

  const payload = {};

  const setIfChanged = (key, nextValue) => {
    const prev = original[key];
    if (prev !== nextValue) payload[key] = nextValue;
  };

  // Campos permitidos por endpoint
  setIfChanged('name', normalizeOptionalString(current.name));
  setIfChanged('lastname', normalizeOptionalString(current.lastname));
  setIfChanged('email', normalizeOptionalString(current.email));

  // Campos del formulario (no listados en el spec compartido, pero requeridos por UI)
  setIfChanged('alias', normalizeOptionalString(current.alias));
  setIfChanged('dni', normalizeOptionalString(current.dni));

  setIfChanged('razon_social', normalizeOptionalString(current.razon_social));
  setIfChanged('cuit', normalizeOptionalString(current.cuit));
  setIfChanged('direccion', normalizeOptionalString(current.direccion));
  setIfChanged('codigo_postal', normalizeOptionalString(current.codigo_postal));
  setIfChanged('provincia_id', toNullableNumber(current.provincia_id));
  setIfChanged('ciudad', normalizeOptionalString(current.ciudad));
  setIfChanged('celular', normalizeOptionalString(current.celular));
  setIfChanged('como_nos_conocieron_id', toNullableNumber(current.como_nos_conocieron_id));

  // Estos campos no están en el formulario hoy, pero si aparecen en `perfil` (o se agregan luego), los soportamos
  if (Object.hasOwn(current, 'acepta_terminos')) {
    setIfChanged('acepta_terminos', Boolean(current.acepta_terminos));
  }
  if (Object.hasOwn(current, 'acepta_ddjj')) {
    setIfChanged('acepta_ddjj', Boolean(current.acepta_ddjj));
  }

  return payload;
};

const handleSave = async () => {
  try {
    const payload = buildUpdateProfilePayloadDiff();
    if (!payload || Object.keys(payload).length === 0) {
      showAlert('info', 'No hay cambios para guardar.');
      return;
    }

    if (process.env.NODE_ENV !== 'production') {
      console.log('[usuarios/perfil] payload', payload);
    }

    await usuarioModel.updateProfile(payload);

    // Mantener store y snapshot en sync
    await usuarioStore.getSession();

    // Rehidratar `perfil` desde session y refrescar snapshot
    const user = usuarioStore.user ?? {};
    const nestedPerfil = user.perfil ?? {};
    const comoNosConocieron = normalizeComoNosConocieronId(
      user.como_nos_conocieron_id ?? nestedPerfil.como_nos_conocieron_id ?? perfil.value.como_nos_conocieron_id
    );
    perfil.value = {
      ...perfil.value,
      ...user,
      ...nestedPerfil,
      como_nos_conocieron_id: comoNosConocieron,
    };
    perfilOriginal.value = deepClone(perfil.value);

    showAlert('success', 'Perfil actualizado correctamente.');
  } catch (error) {
    const message = error?.response?.data?.message || error?.response?.data?.error || 'No se pudo actualizar el perfil.';
    showAlert('danger', message);
  }
};

const handleCancel = () => {
  // Implementar lógica de cancelación
  console.log('Cancelando cambios');
};

const addNewFacturacion = () => {
  if (facturacionLimitReached.value) {
    showAlert('danger', `Solo podés tener hasta ${MAX_FACTURACION_PERFILES} perfiles de facturación.`);
    return;
  }
  router.push({ name: 'FacturacionNuevo' });
};

const editFacturacion = (factura) => {
  if (!factura?.id) return;
  router.push({ name: 'FacturacionNuevo', query: { id: String(factura.id) } });
};

</script>


<template>
  <div class="user-perfil-page">
    <!-- Alerta superior -->
    <div v-if="showTopAlert" class="alert-banner bg-warning-custom">
      <div class="container d-flex align-items-center justify-content-between py-2">
        <div class="d-flex align-items-center">
          <i class="bi bi-bell-fill me-2 text-warning-dark"></i>
          <p class="mb-0 text-warning-dark">
            <span class="fw-medium">¡Atención!</span>
            A partir de ahora es necesarios que ingreses los siguientes datos: Constancia de CUIT, CM05 e ingresos brutos.
          </p>
        </div>
        <button @click="closeAlert" class="btn-close-custom" type="button" aria-label="Cerrar">
          <i class="bi bi-x"></i>
        </button>
      </div>
    </div>

    <div class="container mt-5 mb-5">
      <!-- Título -->
      <h1 class="text-center fw-medium mb-5" style="font-size: 35px;">Perfil</h1>

      <!-- Tarjeta de Información del Usuario -->
      <div class="card shadow-sm mb-4">
        <div class="card-body p-4">
          <div class="row align-items-center">
            <div class="col-auto">
              <div class="avatar-container position-relative">
                <div class="rounded-circle d-flex align-items-center justify-content-center profile-avatar">
                  <img v-if="perfil.foto_url" :src="perfil.foto_url" alt="Perfil" class="rounded-circle w-100 h-100" style="object-fit: cover;" />
                  <span v-else class="initials-text">{{ initialsText }}</span>
                </div>
                <button class="avatar-edit-btn" aria-label="Editar foto" @click="openPhotoModal">
                  <svg xmlns="http://www.w3.org/2000/svg" width="42" height="42" viewBox="0 0 42 42" fill="none">
                    <path d="M21 42C32.598 42 42 32.598 42 21C42 9.40202 32.598 0 21 0C9.40202 0 0 9.40202 0 21C0 32.598 9.40202 42 21 42Z" fill="#EEEEEE"/>
                    <path d="M12.889 13.278V12.618C12.889 12.5313 12.9061 12.4454 12.9394 12.3653C12.9726 12.2853 13.0213 12.2125 13.0827 12.1513C13.1441 12.0901 13.217 12.0416 13.2972 12.0086C13.3774 11.9756 13.4633 11.9587 13.55 11.959H16.187C16.362 11.9593 16.5297 12.0289 16.6534 12.1526C16.7771 12.2763 16.8467 12.444 16.847 12.619V13.279H18.166L20.25 12.238C20.6154 12.0551 21.0184 11.9596 21.427 11.959H28.721C29.4208 11.9593 30.0919 12.2374 30.5868 12.7322C31.0816 13.2271 31.3597 13.8982 31.36 14.598V27.791C31.3595 28.4907 31.0813 29.1617 30.5865 29.6565C30.0917 30.1513 29.4207 30.4295 28.721 30.43H12.889C12.1892 30.4297 11.5181 30.1516 11.0232 29.6567C10.5284 29.1619 10.2503 28.4908 10.25 27.791V15.917C10.2499 15.5704 10.318 15.2272 10.4506 14.9069C10.5832 14.5867 10.7776 14.2957 11.0227 14.0506C11.2677 13.8056 11.5587 13.6112 11.8789 13.4786C12.1992 13.346 12.5424 13.2779 12.889 13.278ZM19.35 15.637C18.9844 15.8215 18.5805 15.9174 18.171 15.917H12.889V18.559H17.622C18.5584 17.9145 19.6683 17.5694 20.805 17.5694C21.9417 17.5694 23.0516 17.9145 23.988 18.559H28.721V14.598H21.427L19.35 15.637ZM20.81 19.546C20.0925 19.546 19.391 19.7588 18.7944 20.1574C18.1978 20.5561 17.7328 21.1227 17.4582 21.7856C17.1836 22.4485 17.1117 23.178 17.2517 23.8818C17.3917 24.5855 17.7372 25.232 18.2446 25.7394C18.752 26.2468 19.3984 26.5923 20.1022 26.7323C20.806 26.8723 21.5354 26.8004 22.1984 26.5258C22.8613 26.2512 23.4279 25.7862 23.8266 25.1896C24.2252 24.593 24.438 23.8915 24.438 23.174C24.4381 22.697 24.3442 22.2248 24.1616 21.7841C23.9791 21.3435 23.7114 20.9433 23.374 20.6062C23.0365 20.2691 22.636 20.0019 22.1951 19.8198C21.7543 19.6377 21.2819 19.5443 20.805 19.545L20.81 19.546Z" fill="#747474"/>
                  </svg>
                </button>
              </div>
            </div>
            <div class="col">
              <h2 class="mb-2" style="font-size: 30px;">Hola, {{ displayName }}</h2>
              <p class="text-muted mb-1" style="font-size: 20px;">{{ perfil.name }} {{ perfil.lastname }}</p>
              <p class="text-muted" style="font-size: 20px;">ID {{ perfil.id || '0' }}</p>
            </div>
            <div class="col-auto">
              <button type="button" class="btn btn-edit me-2" @click="openPhotoModal">
                <i class="bi bi-pencil me-1"></i>
                Editar
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Datos personales -->
      <div class="card shadow-sm mb-4">
        <div class="card-body p-4">
          <h3 class="mb-4 fw-medium" style="font-size: 30px;">Datos personales</h3>

          <div class="row mb-3">
            <div class="col-md-4">
              <label class="form-label fw-bold" for="perfil-name">Nombre</label>
              <input id="perfil-name" type="text" class="form-control custom-input" v-model="perfil.name" placeholder="Sebastián">
            </div>
            <div class="col-md-4">
              <label class="form-label fw-bold" for="perfil-lastname">Apellido</label>
              <input id="perfil-lastname" type="text" class="form-control custom-input" v-model="perfil.lastname" placeholder="Di Tomaso">
            </div>
            <div class="col-md-4">
              <label class="form-label fw-bold" for="perfil-alias">Alias</label>
              <input id="perfil-alias" type="text" class="form-control custom-input" v-model="perfil.alias" placeholder="Alias">
            </div>
          </div>

          <div class="row mb-3">
            <div class="col-md-6">
              <label class="form-label fw-bold" for="perfil-dni">DNI</label>
              <input id="perfil-dni" type="text" class="form-control custom-input" v-model="perfil.dni" placeholder="DNI">
            </div>
            <div class="col-md-6">
              <label class="form-label fw-bold" for="perfil-id">N° de cliente</label>
              <input id="perfil-id" type="text" class="form-control custom-input" v-model="perfil.id" disabled placeholder="2467">
            </div>
          </div>

          <div class="row mb-3">
            <div class="col-md-6">
              <label class="form-label fw-bold" for="perfil-email">Email</label>
              <input id="perfil-email" type="email" class="form-control custom-input" v-model="perfil.email" placeholder="Email">
            </div>
            <div class="col-md-6">
              <div class="d-flex justify-content-between align-items-center">
                <label class="form-label fw-bold mb-0" for="perfil-password">Contraseña</label>
                <a
                  href="#"
                  class="text-primary text-decoration-underline"
                  style="white-space: nowrap; font-size: 13px;"
                  @click="openPasswordModal"
                >
                  Cambiar contraseña
                </a>
              </div>
              <input
                id="perfil-password"
                type="password"
                class="form-control custom-input mt-2"
                placeholder="****************"
                value="****************"
                disabled
              >
            </div>
          </div>


          <div class="row mb-4">
            <div class="col-md-6">
              <label class="form-label fw-medium" for="perfil-como-nos">¿Cómo te enteraste de Adrián Mercado?*</label>
              <select id="perfil-como-nos" class="form-select custom-input" v-model="perfil.como_nos_conocieron_id">
                <option value="" disabled>Seleccionar</option>
                <option v-for="opcion in opcionesComoNosConocieron" :key="opcion.id" :value="String(opcion.id)">
                  {{ opcion.nombre }}
                </option>
              </select>
            </div>
          </div>

          <!-- Botones -->
          <div class="d-flex justify-content-end mt-4">
            <button class="btn btn-cancel me-2" @click="handleCancel">Cancelar</button>
            <button class="btn btn-add-new" @click="handleSave">Guardar</button>
          </div>
        </div>
      </div>

      <!-- Datos de facturación -->
      <div class="card shadow-sm mb-4">
        <div class="card-body p-4">
          <h3 class="mb-4 fw-medium" style="font-size: 30px;">Perfiles de facturación</h3>

          <!-- Cada sección de facturación -->
          <div v-for="(factura, index) in facturacionData" :key="factura?.id ?? index" class="facturacion-section mb-5">
            <div class="factura-top">
              <div class="factura-title">
                <div class="factura-name">{{ factura.nombre_empresa }}</div>
                <span class="iva-badge" :class="ivaBadge(factura).class">{{ ivaBadge(factura).text }}</span>
              </div>

              <div class="factura-actions">
                <label class="predeterminada">
                  <input type="checkbox" class="form-check-input" :checked="factura.isPredeterminada" disabled />
                  <span>Predeterminada</span>
                </label>
                <button type="button" class="btn btn-edit-small" @click="editFacturacion(factura)">
                  <i class="bi bi-pencil me-1"></i>
                  Editar
                </button>
              </div>
            </div>

            <hr class="mb-4" />

            <div class="factura-details-grid">
              <div class="factura-detail">
                <div class="factura-detail__label">CUIT / CUIL</div>
                <div class="factura-detail__value">{{ factura.cuit }}</div>
              </div>
              <div class="factura-detail">
                <div class="factura-detail__label">Teléfono fijo</div>
                <div class="factura-detail__value">{{ factura.telefono_fijo }}</div>
              </div>
              <div class="factura-detail">
                <div class="factura-detail__label">Teléfono celular</div>
                <div class="factura-detail__value">{{ factura.telefono_celular }}</div>
              </div>
              <div class="factura-detail">
                <div class="factura-detail__label">Dirección</div>
                <div class="factura-detail__value">{{ factura.direccion }}</div>
              </div>
              <div class="factura-detail">
                <div class="factura-detail__label">Código postal</div>
                <div class="factura-detail__value">{{ factura.codigo_postal }}</div>
              </div>
              <div class="factura-detail">
                <div class="factura-detail__label">Provincia</div>
                <div class="factura-detail__value">{{ factura.provincia }}</div>
              </div>
              <div class="factura-detail">
                <div class="factura-detail__label">Localidad</div>
                <div class="factura-detail__value">{{ factura.localidad }}</div>
              </div>
            </div>

            <div class="archivos-box">
              <div class="archivos-title">Archivos cargados</div>
              <div class="archivos-grid">
                <div
                  v-for="item in archivoLabelByTipo((factura?.tipo === 'responsable' ? 'responsable' : 'consumidor'))"
                  :key="item.key"
                  class="archivo-item"
                >
                  <div class="archivo-label">{{ item.label }}</div>
                  <div
                    class="archivo-status"
                    :class="{ 'archivo-status--ok': archivoStatus(factura?.archivos?.[item.key]).ok, 'archivo-status--missing': !archivoStatus(factura?.archivos?.[item.key]).ok }"
                  >
                    <svg
                      v-if="archivoStatus(factura?.archivos?.[item.key]).ok"
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M7 14C7.91925 14 8.8295 13.8189 9.67878 13.4672C10.5281 13.1154 11.2997 12.5998 11.9497 11.9497C12.5998 11.2997 13.1154 10.5281 13.4672 9.67878C13.8189 8.8295 14 7.91925 14 7C14 6.08075 13.8189 5.1705 13.4672 4.32122C13.1154 3.47194 12.5998 2.70026 11.9497 2.05025C11.2997 1.40024 10.5281 0.884626 9.67878 0.532843C8.8295 0.18106 7.91925 -1.36979e-08 7 0C5.14348 2.76642e-08 3.36301 0.737498 2.05025 2.05025C0.737498 3.36301 0 5.14348 0 7C0 8.85652 0.737498 10.637 2.05025 11.9497C3.36301 13.2625 5.14348 14 7 14ZM6.81956 9.83111L10.7084 5.16444L9.51378 4.16889L6.16933 8.18144L4.43878 6.45011L3.339 7.54989L5.67233 9.88322L6.27433 10.4852L6.81956 9.83111Z"
                        fill="#006C4C"
                      />
                    </svg>
                    <svg
                      v-else
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <path
                        d="M7 0C10.8661 0 14 3.1339 14 7C14 10.8661 10.8661 14 7 14C3.1339 14 0 10.8661 0 7C0 3.1339 3.1339 0 7 0ZM7 9.1C6.81435 9.1 6.6363 9.17375 6.50503 9.30503C6.37375 9.4363 6.3 9.61435 6.3 9.8C6.3 9.98565 6.37375 10.1637 6.50503 10.295C6.6363 10.4263 6.81435 10.5 7 10.5C7.18565 10.5 7.3637 10.4263 7.49497 10.295C7.62625 10.1637 7.7 9.98565 7.7 9.8C7.7 9.61435 7.62625 9.4363 7.49497 9.30503C7.3637 9.17375 7.18565 9.1 7 9.1ZM7 2.8C6.82855 2.80002 6.66306 2.86297 6.53494 2.9769C6.40681 3.09083 6.32496 3.24782 6.3049 3.4181L6.3 3.5V7.7C6.3002 7.87842 6.36852 8.05002 6.49099 8.17976C6.61347 8.30949 6.78087 8.38756 6.95898 8.39802C7.13709 8.40848 7.31247 8.35053 7.44928 8.23601C7.5861 8.1215 7.67403 7.95907 7.6951 7.7819L7.7 7.7V3.5C7.7 3.31435 7.62625 3.1363 7.49497 3.00503C7.3637 2.87375 7.18565 2.8 7 2.8Z"
                        fill="#FF4D4D"
                      />
                    </svg>
                    <span :class="{ 'text-danger': !archivoStatus(factura?.archivos?.[item.key]).ok }">
                      {{ archivoStatus(factura?.archivos?.[item.key]).text }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Botón Agregar nueva -->
          <div class="mt-4 d-flex justify-content-start">
            <button class="btn btn-add-new" :disabled="facturacionLimitReached" @click="addNewFacturacion">
              Agregar nueva
            </button>
          </div>
          <div v-if="facturacionLimitReached" class="mt-2 text-muted" style="font-size: 13px;">
            Alcanzaste el máximo de perfiles de facturación ({{ MAX_FACTURACION_PERFILES }}).
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: Cambiar contraseña -->
    <div
      class="modal fade"
      tabindex="-1"
      aria-hidden="true"
      ref="passwordModalRef"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Cambiar contraseña</h5>
            <button type="button" class="btn-close" aria-label="Close" @click="closePasswordModal" :disabled="passwordSaving"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label fw-bold" for="current-password">Contraseña actual</label>
              <div class="input-group">
                <input
                  id="current-password"
                  :type="showCurrentPassword ? 'text' : 'password'"
                  class="form-control custom-input"
                  v-model="passwordForm.password"
                  placeholder="Ingresá tu contraseña actual"
                  :disabled="passwordSaving"
                />
                <button
                  class="btn btn-outline-secondary password-toggle-btn"
                  type="button"
                  @click="showCurrentPassword = !showCurrentPassword"
                  :disabled="passwordSaving"
                  aria-label="Mostrar/ocultar contraseña"
                >
                  <i :class="showCurrentPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
                </button>
              </div>
            </div>
            <div class="mb-3">
              <label class="form-label fw-bold" for="new-password">Nueva contraseña</label>
              <div class="input-group">
                <input
                  id="new-password"
                  :type="showNewPassword ? 'text' : 'password'"
                  class="form-control custom-input"
                  v-model="passwordForm.new_password"
                  placeholder="Mínimo 6 caracteres"
                  :disabled="passwordSaving"
                />
                <button
                  class="btn btn-outline-secondary password-toggle-btn"
                  type="button"
                  @click="showNewPassword = !showNewPassword"
                  :disabled="passwordSaving"
                  aria-label="Mostrar/ocultar contraseña"
                >
                  <i :class="showNewPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
                </button>
              </div>
              <small class="text-muted">Debe tener al menos 6 caracteres.</small>
            </div>
            <div class="mb-3">
              <label class="form-label fw-bold" for="confirm-password">Confirmar nueva contraseña</label>
              <div class="input-group">
                <input
                  id="confirm-password"
                  :type="showConfirmPassword ? 'text' : 'password'"
                  class="form-control custom-input"
                  v-model="passwordForm.new_password_confirmation"
                  placeholder="Repetí la nueva contraseña"
                  :disabled="passwordSaving"
                />
                <button
                  class="btn btn-outline-secondary password-toggle-btn"
                  type="button"
                  @click="showConfirmPassword = !showConfirmPassword"
                  :disabled="passwordSaving"
                  aria-label="Mostrar/ocultar contraseña"
                >
                  <i :class="showConfirmPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
                </button>
              </div>
            </div>

            <div v-if="passwordSaving" class="d-flex align-items-center mt-3">
              <div class="spinner-border spinner-border-sm me-2" aria-hidden="true"></div>
              <output class="text-muted" aria-live="polite">Cambiando contraseña…</output>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closePasswordModal" :disabled="passwordSaving">
              Cancelar
            </button>
            <button type="button" class="btn btn-primary" @click="openConfirmPasswordModal" :disabled="passwordSaving">
              Guardar
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: Confirmación de cambio de contraseña -->
    <div
      class="modal fade"
      tabindex="-1"
      aria-hidden="true"
      ref="confirmPasswordModalRef"
    >
      <div class="modal-dialog modal-dialog-centered modal-sm">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Confirmar cambio</h5>
            <button type="button" class="btn-close" aria-label="Close" @click="closeConfirmPasswordModal"></button>
          </div>
          <div class="modal-body">
            <p class="mb-0">¿Estás seguro de que querés cambiar tu contraseña?</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeConfirmPasswordModal">
              No, cancelar
            </button>
            <button type="button" class="btn btn-primary" @click="submitChangePassword">
              Sí, cambiar
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: Foto de perfil -->
    <div
      class="modal fade"
      tabindex="-1"
      aria-hidden="true"
      ref="photoModalRef"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Actualizar foto de perfil</h5>
            <button type="button" class="btn-close" aria-label="Close" @click="closePhotoModal"></button>
          </div>
          <div class="modal-body">
            <div class="d-flex align-items-center gap-3">
              <div class="photo-preview-editor">
                <div
                  class="photo-preview-editor__frame"
                  @pointerdown="onEditorPointerDown"
                  @pointermove="onEditorPointerMove"
                  @pointerup="onEditorPointerUp"
                  @pointercancel="onEditorPointerUp"
                >
                  <img
                    v-if="photoPreview"
                    :src="photoPreview"
                    alt="Preview"
                    class="photo-preview-editor__img"
                    :style="{
                      transform: `translate(${photoOffsetX}px, ${photoOffsetY}px) scale(${photoScale})`,
                    }"
                    draggable="false"
                  />
                  <div v-else class="photo-preview-editor__placeholder">{{ initialsText }}</div>
                </div>

                <div v-if="photoPreview" class="photo-preview-editor__controls mt-2">
                  <label class="form-label mb-1" for="photoZoomRange">Zoom</label>
                  <input
                    id="photoZoomRange"
                    type="range"
                    class="form-range"
                    min="1"
                    max="2.5"
                    step="0.01"
                    v-model.number="photoScale"
                    @input="onZoomInput"
                    :disabled="photoUploading"
                  />
                  <button type="button" class="btn btn-sm btn-outline-secondary" @click="resetPhotoEditor" :disabled="photoUploading">
                    Centrar
                  </button>
                </div>
              </div>
              <div class="flex-grow-1">
                <FileInput
                  v-model="selectedPhotoFile"
                  accept="image/*"
                  label="Seleccionar imagen"
                  :disabled="photoUploading"
                  help="Formatos: JPG/PNG. Podés arrastrar y hacer zoom para centrar."
                  @change="onPhotoFileSelected"
                  @clear="resetPhotoModalState"
                />
              </div>
            </div>

            <div v-if="photoUploading" class="d-flex align-items-center mt-3">
              <div class="spinner-border spinner-border-sm me-2" aria-hidden="true"></div>
              <output class="text-muted" aria-live="polite">Procesando imagen…</output>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closePhotoModal" :disabled="photoUploading">
              Cancelar
            </button>
            <button type="button" class="btn btn-primary" @click="saveProfilePhoto" :disabled="photoUploading || !photoPreview">
              Guardar foto
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>


<style scoped>
.user-perfil-page {
  background-color: #f8f9fa;
  min-height: 100vh;
  font-family: 'Roboto', sans-serif;
}

/* Alerta superior */
.alert-banner {
  width: 100%;
}

.bg-warning-custom {
  background-color: #fff3cd;
  border-bottom: 1px solid #ffc107;
}

.text-warning-dark {
  color: #9f5b00;
}

.btn-close-custom {
  background: none;
  border: none;
  font-size: 20px;
  color: #9f5b00;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-close-custom:hover {
  opacity: 0.7;
}

/* Avatar */
.avatar-container {
  position: relative;
  display: inline-block;
}

.profile-avatar {
  width: 165px;
  height: 165px;
  background: linear-gradient(135deg, #e8eef5 0%, #d4dfe9 100%);
  border: 3px solid #fff;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  position: relative;
}

.initials-text {
  font-size: 48px;
  font-weight: 500;
  color: #375DBF;
}

.avatar-edit-btn {
  position: absolute;
  bottom: 10px;
  right: 10px;
  width: 42px;
  height: 42px;
  background: white;
  border: 2px solid #e0e0e0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
  transition: all 0.2s;
}

.avatar-edit-btn:hover {
  background: #f8f9fa;
  transform: scale(1.05);
}

.avatar-edit-btn i {
  font-size: 16px;
  color: #666;
}

/* Modal foto */
.photo-preview-editor {
  width: 220px;
}

.photo-preview-editor__frame {
  width: 220px;
  height: 220px;
  border-radius: 50%;
  overflow: hidden;
  background: #eef2f6;
  border: 2px solid #e0e0e0;
  position: relative;
  user-select: none;
  touch-action: none;
  cursor: grab;
  display: flex;
  align-items: center;
  justify-content: center;
}

.photo-preview-editor__frame:active {
  cursor: grabbing;
}

.photo-preview-editor__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transform-origin: center;
  pointer-events: none;
}

.photo-preview-editor__placeholder {
  font-size: 22px;
  font-weight: 600;
  color: #375dbf;
}

/* Botones */
.btn-edit,
.btn-edit-small {
  background-color: #f6f8f9;
  border: none;
  border-radius: 3px;
  padding: 8px 10px;
  font-size: 15px;
  font-weight: 500;
  color: #000;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-edit:hover,
.btn-edit-small:hover {
  background-color: #e8ecef;
}

.btn-edit i,
.btn-edit-small i {
  font-size: 15px;
}

.btn-cancel {
  background-color: #e9ecef;
  border: 1px solid #e9ecef;
  color: #374151;
  padding: 10px 24px;
  border-radius: 4px;
  font-size: 16px;
  font-weight: bold;
  cursor: not-allowed;
}

.btn-save {
  background-color: #375DBF;
  border: 1px solid #375DBF;
  color: #fff;
  padding: 10px 24px;
  border-radius: 4px;
  font-size: 16px;
  font-weight: bold;
}

.btn-add-new {
  background-color: #375DBF;
  border: none;
  color: white;
  padding: 10px 24px;
  border-radius: 4px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-add-new:hover {
  background-color: #2d4a99;
}

.btn-add-new:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-add-new:disabled:hover {
  background-color: #375DBF;
}

/* Inputs personalizados */
.custom-input {
  border-radius: 4px !important;
  padding: 10px 16px;
  font-size: 13px;
  background-color: #fff;
  border: 1px solid #bbb !important;
}

.custom-input:focus {
  border-color: #375DBF;
  box-shadow: 0 0 0 0.2rem rgba(55, 93, 191, 0.15);
  outline: none;
}

.custom-input:disabled {
  background-color: #f6f8f9;
  border-color: #ddd;
  color: #666;
}

.form-label {
  font-size: 13px;
  margin-bottom: 8px;
  color: #000;
}

/* Sección de facturación */
.facturacion-section {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 30px;
}

.facturacion-section:last-of-type {
  border-bottom: none;
}

.facturacion-section hr {
  border-color: #e0e0e0;
  margin: 16px 0;
}

.facturacion-section p {
  color: #000;
  font-size: 13px;
}

.factura-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
}

.factura-title {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.factura-name {
  font-size: 18px;
  font-weight: 600;
  color: #000;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.iva-badge {
  font-size: 11px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 4px;
  line-height: 1.2;
  white-space: nowrap;
}

.iva-badge--responsable {
  background: #d8ebf7;
  color: #1c5f8a;
}

.iva-badge--consumidor {
  background: #f5d2b2;
  color: #7a3b0c;
}

.factura-actions {
  display: inline-flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
}

.predeterminada {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #000;
}

.factura-details-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 18px;
  margin-bottom: 18px;
}

.factura-detail__label {
  font-size: 12px;
  font-weight: 700;
  color: #000;
  margin-bottom: 6px;
}

.factura-detail__value {
  font-size: 13px;
  color: #000;
  word-break: break-word;
}

.archivos-box {
  background: #f3f5f7;
  border-radius: 6px;
  padding: 14px 16px;
}

.archivos-title {
  font-size: 13px;
  font-weight: 700;
  color: #000;
  margin-bottom: 10px;
}

.archivos-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.archivo-label {
  font-size: 12px;
  font-weight: 700;
  color: #000;
  margin-bottom: 6px;
}

.archivo-status {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #000;
}

.archivo-status svg {
  flex-shrink: 0;
  display: block;
}

@media (max-width: 1200px) {
  .factura-details-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .factura-top {
    align-items: flex-start;
    flex-direction: column;
  }

  .factura-actions {
    width: 100%;
    justify-content: space-between;
  }

  .factura-details-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .archivos-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 576px) {
  .facturacion-section {
    padding-bottom: 24px;
  }

  .factura-title {
    width: 100%;
    flex-wrap: wrap;
    gap: 10px;
  }

  .factura-name {
    font-size: 20px;
    white-space: normal;
  }

  .iva-badge {
    font-size: 12px;
    padding: 6px 12px;
  }

  .factura-actions {
    width: 100%;
    justify-content: space-between;
    gap: 12px;
  }

  .predeterminada {
    font-size: 14px;
    font-weight: 600;
  }

  .facturacion-section hr {
    margin: 12px 0 18px;
  }

  .factura-details-grid {
    grid-template-columns: 1fr;
    gap: 18px;
    margin-bottom: 22px;
  }

  .factura-detail__label {
    font-size: 13px;
    margin-bottom: 8px;
  }

  .factura-detail__value {
    font-size: 14px;
  }

  .archivos-box {
    padding: 18px 16px;
  }

  .archivos-title {
    font-size: 14px;
    margin-bottom: 14px;
  }

  .archivo-label {
    font-size: 13px;
    margin-bottom: 10px;
  }

  .archivo-status {
    font-size: 13px;
  }

  .btn-add-new {
    width: 260px;
    max-width: 100%;
    padding: 12px 18px;
  }
}

/* File upload display */
.file-upload-display {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
}

.file-upload-display span {
  font-size: 13px;
  color: #000;
  flex: 1;
}

.btn-remove-file,
.btn-upload-file {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 15px;
  margin-left: 8px;
}

.btn-remove-file {
  color: #ff4d4d;
}

/* Password toggle */
.password-toggle-btn {
  border-color: #bbb;
  background: #fff;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
}
.password-toggle-btn:hover {
  background: #f6f8f9;
  color: #333;
}

.btn-upload-file {
  color: #52e5ba;
}

.btn-remove-file:hover,
.btn-upload-file:hover {
  opacity: 0.7;
}

/* Checkbox personalizado */
.form-check-input {
  width: 16px;
  height: 16px;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  display: inline-block;
  border: 2px solid #000;
  border-radius: 3px;
  cursor: pointer;
  background-color: #fff;
  background-position: center;
  background-repeat: no-repeat;
  background-size: 12px 12px;
}

.form-check-input:checked {
  background-color: #375DBF;
  border-color: #375DBF;
  /* Force checkmark even when disabled */
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23fff' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M3.5 8.5l3 3 6-7'/%3e%3c/svg%3e");
}

/* Algunos estilos globales quitan el background-image en disabled; lo forzamos para el caso checked+disabled */
.predeterminada .form-check-input:checked:disabled {
  background-color: #375DBF;
  border-color: #375DBF;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23fff' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M3.5 8.5l3 3 6-7'/%3e%3c/svg%3e");
}

.form-check-input:disabled {
  opacity: 1;
}

/* Card styling */
.card {
  background-color: white;
  border: none;
  border-radius: 6px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.card-body {
  padding: 2rem;
}

/* Responsive */
@media (max-width: 768px) {
  .profile-avatar {
    width: 120px;
    height: 120px;
  }

  .initials-text {
    font-size: 36px;
  }

  .avatar-edit-btn {
    width: 36px;
    height: 36px;
  }

  .avatar-edit-btn i {
    font-size: 14px;
  }

  h1 {
    font-size: 28px !important;
  }

  h2,
  h3 {
    font-size: 24px !important;
  }
}
</style>
